import React, { useState, useEffect } from 'react';
import UpgradeModal from './UpgradeModal';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';

export default function PlanUsageCard({
  chatbotUsed = 4,
  chatbotTotal = 5,
  researchUsed = 1,
  researchTotal = 1,
  docUsed = 0,
  docTotal = 1,
  isDarkMode,
  hideUpgradeButton = false,
  onUpgradeFlowComplete,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { subscription, subscriptionLoading } = useSelector(
    (state) => state.auth,
  );

  const getProgressWidth = (used, total) => `${(used / total) * 100}%`;

  // ---- PLAN NAME & STATUS (SUBSCRIPTION ONLY) ----
  const planName = subscription?.package_name || 'Free';
  const status = subscription?.status || 'free';

  let titleText = `${planName} Plan Usage`;

  if (status === 'pending') {
    titleText = 'Activating your plan';
  } else if (status === 'paused') {
    titleText = `${planName} Plan Usage (Paused)`;
  }

  const isFreePlan =
    !subscriptionLoading &&
    (!subscription || planName === 'Free' || status === 'free');
  useEffect(() => {
    if (subscriptionLoading) return; // ⛔ wait for truth

    const intent = sessionStorage.getItem('upgrade_intent');
    if (!intent) return;

    if (!isFreePlan) {
      toast.error('You already have an active plan.');
      sessionStorage.removeItem('upgrade_intent');
      sessionStorage.removeItem('from_upgrade_intent');
      return;
    }

    if (!sessionStorage.getItem('upgrade_modal_open')) {
      sessionStorage.setItem('upgrade_modal_open', 'true');
      setIsOpen(true);
    }
  }, [isFreePlan, subscriptionLoading]);
  useEffect(() => {
    // 🔥 If page refreshed, modal is not actually open
    sessionStorage.removeItem('from_upgrade_intent');
    sessionStorage.removeItem('upgrade_modal_open');
  }, []);

  const usageData = [
    { label: 'AI Chat Queries', used: chatbotUsed, total: chatbotTotal },
    {
      label: 'Professional Research',
      used: researchUsed,
      total: researchTotal,
    },
    { label: 'Document Check', used: docUsed, total: docTotal },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="bg-brand rounded-md p-3 sm:p-4 shadow-md text-white text-[10px] sm:text-xs w-full mx-auto"
    >
      <h3 className="text-[10px] sm:text-[11px] font-semibold mb-2 sm:mb-3 text-center border-b border-white pb-1">
        {titleText}
      </h3>

      {usageData.map((item, idx) => (
        <div key={idx} className="mb-1.5   sm:mb-2">
          <div className="flex  justify-between text-[10px] sm:text-[11px] leading-none mb-0.5">
            <span>{item.label}</span>
            <span>
              {item.used}/{item.total}
            </span>
          </div>
          <div className="w-full bg-white/30 rounded h-[4px] sm:h-1 overflow-hidden">
            <motion.div
              className="bg-white h-full rounded"
              initial={{ width: 0 }}
              animate={{ width: getProgressWidth(item.used, item.total) }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
        </div>
      ))}

      {/* {!subscription?.square_subscription_id && (
        <button
          onClick={() => setIsOpen(true)}
          className="mt-2 sm:mt-4 w-full bg-white text-black text-[10px] sm:text-[11px] font-semibold py-1 sm:py-1.5 rounded hover:bg-brand border border-white hover:text-white transition-all"
        >
          Upgrade Plan
        </button>
      )} */}

      {isFreePlan && !hideUpgradeButton && (
        <motion.button
          onClick={() => setIsOpen(true)}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="mt-2 sm:mt-4 w-full bg-white text-black text-[10px] sm:text-[11px]
               font-semibold py-1 sm:py-1.5 rounded
               hover:bg-brand border border-white hover:text-white"
        >
          Upgrade Plan
        </motion.button>
      )}

      <UpgradeModal
        isOpen={isOpen}
        onClose={() => {
          const cameFromUpgrade = sessionStorage.getItem('from_upgrade_intent');

          sessionStorage.removeItem('upgrade_intent');
          sessionStorage.removeItem('upgrade_modal_open');
          sessionStorage.removeItem('from_upgrade_intent');

          setIsOpen(false);

          if (cameFromUpgrade) {
            onUpgradeFlowComplete();
          }
        }}
        isDarkMode={isDarkMode}
      />
    </motion.div>
  );
}
